import Exceljs from 'exceljs'
import fileDownload from 'js-file-download'

// 设置单元格样式
function setRowCellAlignment(row, options = { vertical: 'middle' }) {
  row.eachCell(cell => {
    cell.alignment = { ...cell.alignment, ...options }
  })
}

/**
 * 下载简单表格
 * @param {*} tableData 表格数据
 * @param {*} headerColumns 表格头 name: 表头名称 key: 表头key width: 表头宽度
 * @param {*} fileName 文件名
 * @param {*} styleCallback 样式回调
 * ExcelJS文档地址：https://github.com/exceljs/exceljs/blob/master/README_zh.md
 */
const downloadExcel = async ({ tableData = [], columns = [], fileName = new Date().getTime(), styleCallback, footerData }) => {
  const workbook = new Exceljs.Workbook() // 创建一个新的工作簿

  // 创建tab页,并冻结表头
  const worksheet = workbook.addWorksheet('Sheet 1', {
    views: [
      // 冻结表头
      {
        state: 'frozen',
        ySplit: 1
      }
    ]
  })

  // 设置表头
  worksheet.columns = columns.map(item => {
    return {
      header: item.name,
      key: item.key,
      width: item.width || 20
    }
  })

  // 设置表头样式
  worksheet.getRow(1).font = {
    // color: { argb: 'FFFFFF' },
    // fgColor: { argb: '000000' },
    bold: true
  }

  tableData.forEach(x => {
    worksheet.addRow(x)
  })
  if (footerData) {
    if (Array.isArray(footerData)) {
      footerData.forEach(item => {
        worksheet.addRow(item)
      })
    } else {
      worksheet.addRow(footerData)
    }
  }
  //   worksheet.getRow(1).eachCell((cell, rowNumber) => {
  //     cell.fill = {
  //       type: 'pattern',
  //       pattern: 'darkVertical', // darkTrellis | darkVertical
  //       fgColor: { argb: '000000' },
  //       bgColor: { argb: '000000' }
  //     }
  //   })
  worksheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle' }
    })
  })
  // 自定义内容样式
  styleCallback && styleCallback(worksheet)

  // 生成Excel文件并下载
  const excelBuffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  fileDownload(blob, fileName + '.xlsx')
}

// 获取最大深度
function getDepth(nodes, depth = 1) {
  return nodes.reduce((max, node) => {
    if (node.children) {
      return Math.max(max, getDepth(node.children, depth + 1))
    }
    return max
  }, depth)
}

function renderGroupedHeader(worksheet, nodes, colKeyMap, headerColumns, maxDepth, rowIndex = 1, colPos = 1) {
  nodes.forEach(node => {
    const startCol = colPos
    let colSpan = 1
    let rowSpan = 1

    if (node.children && node.children.length > 0) {
      const childColStart = colPos
      colPos = renderGroupedHeader(worksheet, node.children, colKeyMap, headerColumns, maxDepth, rowIndex + 1, colPos)
      colSpan = colPos - childColStart
      rowSpan = 1
    } else {
      colKeyMap[colPos] = node.key
      headerColumns.push({ name: node.name, key: node.key, width: node.width || 20 })
      colPos++
      rowSpan = maxDepth - rowIndex + 1
    }

    const cell = worksheet.getCell(rowIndex, startCol)
    cell.value = node.name
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
    if (colSpan > 1 || rowSpan > 1) {
      worksheet.mergeCells(rowIndex, startCol, rowIndex + rowSpan - 1, startCol + colSpan - 1)
    }
  })
  return colPos
}

/**
 * 根据嵌套的 columns 导出 Excel（独立实现，不依赖 downloadGroupsExcel）
 * @param {*} tableData 表格数据
 * @param {*} columns 嵌套表头结构（包含 children）
 * @param {*} fileName 文件名
 * @param {*} styleCallback 样式函数
 */
const downloadGroupsExcel = async ({ tableData = [], columns = [], fileName = new Date().getTime(), styleCallback, footerData }) => {
  const workbook = new Exceljs.Workbook()
  const worksheet = workbook.addWorksheet('Sheet 1')

  const headerColumns = []
  const colKeyMap = {}
  let colIndex = 1

  const maxDepth = getDepth(columns)

  renderGroupedHeader(worksheet, columns, colKeyMap, headerColumns, maxDepth)

  // 设置列宽
  worksheet.columns = headerColumns.map(col => ({
    key: col.key,
    width: col.width
  }))

  // 添加数据
  tableData.forEach(row => {
    const rowData = []
    Object.keys(colKeyMap).forEach(colPos => {
      const key = colKeyMap[colPos]
      rowData.push(row[key] || '-')
    })
    worksheet.addRow(rowData)
  })

  if (footerData) {
    // if (Array.isArray(footerData)) {
    footerData.forEach(item => {
      worksheet.addRow(item)
    })
    // } else {
    //   worksheet.addRow(footerData)
    // }
  }

  worksheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle' }
    })
  })
  // 自定义样式
  styleCallback && styleCallback(worksheet)

  // 下载
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  fileDownload(blob, fileName + '.xlsx')
}

/**
 * 根据列分组导出Excel，支持主键合并及子键连续合并
 * @param {Object} params
 * @param {Array} params.tableData 表格数据
 * @param {Array} params.headerColumns 表头列配置
 * @param {string} params.fileName 文件名
 * @param {Function} params.styleCallback 样式回调
 * @param {string} params.mergeKey 主合并列key
 * @param {Array} params.subMergeKeys 子合并列keys
 * @param {string} [params.visibleMergeKey] 可见的合并列key（当mergeKey列隐藏时使用）
 */
const downloadColExcel = async ({
  tableData = [],
  headerColumns = [],
  fileName = new Date().getTime(),
  styleCallback,
  mergeKey,
  subMergeKeys,
  visibleMergeKey
}) => {
  const workbook = new Exceljs.Workbook()
  const worksheet = workbook.addWorksheet('Sheet1')

  const mergeKeyColumn = headerColumns.find(col => col.key === mergeKey)
  const mergeKeyVisible = mergeKeyColumn && !mergeKeyColumn.hidden

  if (!mergeKeyVisible && !visibleMergeKey) {
    throw new Error('mergeKey column is not visible and visibleMergeKey is not provided.')
  }

  let columnsForWorksheet = headerColumns
  if (!mergeKeyVisible && mergeKey) {
    // Insert a hidden column for mergeKey at the beginning
    columnsForWorksheet = [{ header: '', key: mergeKey, width: 0, hidden: true }, ...headerColumns]
  }

  worksheet.columns = columnsForWorksheet.map(item => {
    return {
      header: item.header || item.name || '',
      key: item.key,
      width: item.width || 20,
      hidden: item.hidden || false
    }
  })

  const keyToColIndex = {}
  worksheet.columns.forEach((col, idx) => {
    keyToColIndex[col.key] = idx + 1
  })

  const groupMap = {}
  tableData.forEach(item => {
    const groupVal = item[mergeKey]
    if (!groupMap[groupVal]) groupMap[groupVal] = []
    groupMap[groupVal].push(item)
  })

  // Use visibleMergeKey if provided, else mergeKey
  const visualMergeKey = visibleMergeKey || mergeKey
  const visualMergeColIdx = keyToColIndex[visualMergeKey]

  // 记录当前行号（1-based，Excel第一行是表头，所以数据从2开始）
  let currentRow = 2
  // 遍历分组写入
  for (const groupVal in groupMap) {
    const groupRows = groupMap[groupVal]
    // 写入分组行
    groupRows.forEach(row => {
      worksheet.addRow({ ...row, [mergeKey]: row[mergeKey] })
    })
    // 先合并 visualMergeKey 列（整组合并）
    if (visualMergeColIdx && groupRows.length > 1) {
      worksheet.mergeCells(currentRow, visualMergeColIdx, currentRow + groupRows.length - 1, visualMergeColIdx)
    }
    // subMergeKeys 逐列处理合并，按连续相同值分组（排除 visualMergeKey）
    if (Array.isArray(subMergeKeys)) {
      for (const key of subMergeKeys) {
        if (key === visualMergeKey) continue // 跳过 visualMergeKey，已处理
        const colIdx = keyToColIndex[key]
        if (!colIdx) continue
        let start = currentRow
        let previousValue = groupRows.length > 0 ? groupRows[0][key] : undefined
        for (let i = 1; i <= groupRows.length; i++) {
          let currentValue = i < groupRows.length ? groupRows[i][key] : undefined
          // 如果当前值与前一个不同，或者到了最后一行
          if (currentValue !== previousValue) {
            const end = currentRow + i - 1
            if (end - start + 1 > 1 && previousValue !== undefined) {
              worksheet.mergeCells(start, colIdx, end, colIdx)
            }
            start = currentRow + i
            previousValue = currentValue
          }
        }
      }
    }
    currentRow += groupRows.length
  }
  worksheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle' }
    })
  })
  styleCallback && styleCallback(worksheet)
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  fileDownload(blob, fileName + '.xlsx')
}

/**
 * 根据嵌套分组列结构导出 Excel，支持多级表头、主列合并、子列连续合并以及底部合计数据
 *
 * @param {Object} params 配置参数
 * @param {Array<Object>} params.tableData 表格主体数据
 * @param {Array<Object>} params.columns 表头列定义（支持嵌套 children 结构）
 * @param {string} [params.fileName=时间戳] 导出的 Excel 文件名
 * @param {Function} [params.styleCallback] 可选样式处理回调，接收 worksheet 实例
 * @param {string} params.mergeKey 主合并列 key（以该字段分组，合并相同行）
 * @param {Array<string>} [params.subMergeKeys] 子合并列 keys（对每组内值连续的列进行合并）
 * @param {string} [params.visibleMergeKey] 可选，mergeKey 对应列如果隐藏，可设置此字段为合并显示列
 * @param {Array<Array>} [params.footerData] 可选，底部合计数据行（可传多行）
 *
 * @example
 * downloadGroupsColExcel({
 *   tableData,
 *   columns,
 *   fileName: '报表',
 *   mergeKey: 'categoryId',
 *   visibleMergeKey: 'categoryName',
 *   subMergeKeys: ['subCategory', 'unit'],
 *   footerData: [{ categoryName: '合计', amount: 1000 }],
 *   styleCallback: (worksheet) => {
 *     // 自定义样式处理
 *     worksheet.getRow(1).font = { bold: true }
 *   }
 * })
 */
const downloadGroupsColExcel = async ({
  tableData = [],
  columns = [],
  fileName = new Date().getTime(),
  styleCallback,
  mergeKey,
  subMergeKeys,
  visibleMergeKey,
  footerData
}) => {
  const workbook = new Exceljs.Workbook()
  const worksheet = workbook.addWorksheet('Sheet1')
  const headerColumns = []
  const colKeyMap = {}
  let colIndex = 1

  const maxDepth = getDepth(columns)

  renderGroupedHeader(worksheet, columns, colKeyMap, headerColumns, maxDepth)

  // 设置列宽
  worksheet.columns = headerColumns.map(col => ({
    key: col.key,
    width: col.width
  }))

  const keyToColIndex = {}
  worksheet.columns.forEach((col, idx) => {
    keyToColIndex[col.key] = idx + 1
  })

  const groupMap = {}
  tableData.forEach(item => {
    const groupVal = item[mergeKey]
    if (!groupMap[groupVal]) groupMap[groupVal] = []
    groupMap[groupVal].push(item)
  })

  const visualMergeKey = visibleMergeKey || mergeKey
  const visualMergeColIdx = keyToColIndex[visualMergeKey]

  // 记录当前行号（1-based，Excel第一行是表头，所以数据从2开始）
  let currentRow = maxDepth + 1
  // 遍历分组写入
  for (const groupVal in groupMap) {
    const groupRows = groupMap[groupVal]
    // 写入分组行
    groupRows.forEach(row => {
      worksheet.addRow({ ...row, [mergeKey]: row[mergeKey] })
    })
    // 先合并 visualMergeKey 列（整组合并）
    if (visualMergeColIdx && groupRows.length > 1) {
      worksheet.mergeCells(currentRow, visualMergeColIdx, currentRow + groupRows.length - 1, visualMergeColIdx)
    }
    // subMergeKeys 逐列处理合并，按连续相同值分组（排除 visualMergeKey）
    if (Array.isArray(subMergeKeys)) {
      for (const key of subMergeKeys) {
        if (key === visualMergeKey) continue // 跳过 visualMergeKey，已处理
        const colIdx = keyToColIndex[key]
        if (!colIdx) continue
        let start = currentRow
        let previousValue = groupRows.length > 0 ? groupRows[0][key] : undefined
        for (let i = 1; i <= groupRows.length; i++) {
          let currentValue = i < groupRows.length ? groupRows[i][key] : undefined
          // 如果当前值与前一个不同，或者到了最后一行
          if (currentValue !== previousValue) {
            const end = currentRow + i - 1
            if (end - start + 1 > 1 && previousValue !== undefined) {
              worksheet.mergeCells(start, colIdx, end, colIdx)
            }
            start = currentRow + i
            previousValue = currentValue
          }
        }
      }
    }
    currentRow += groupRows.length
  }
  if (footerData) {
    footerData.forEach(item => {
      worksheet.addRow(item)
    })
  }

  worksheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle' }
    })
  })
  styleCallback && styleCallback(worksheet)

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  fileDownload(blob, fileName + '.xlsx')
}

export default {
  downloadGroupsExcel,
  downloadExcel,
  downloadColExcel,
  downloadGroupsColExcel
}
export { downloadExcel, downloadGroupsExcel, downloadColExcel, downloadGroupsColExcel }
